/**
 * 变体视频处理模块 - 重构版
 * 从描述内容中自动提取 YouTube 视频链接并渲染视频区域
 * 模块化架构，职责清晰，易于维护
 */
(function() {
  'use strict';

  // ==================== ConfigManager ====================
  /**
   * 配置管理器 - 集中管理所有配置常量
   */
  class ConfigManager {
    static SELECTORS = {
      DESCRIPTION_CONTAINER: '#tab-product-description',
      VIDEO_SECTION: '[data-variant-videos]',
      VIDEO_GRID: '.variant-videos-grid',
      VIDEO_ITEM: '.variant-video-item',
      VIDEO_PREVIEW: '.variant-video-preview',
      SCROLL_INDICATOR: '.variant-videos-scroll-indicator',
      NAV_BUTTON: '[data-video-nav]',
      PREV_BUTTON: '[data-video-nav="prev"]',
      NEXT_BUTTON: '[data-video-nav="next"]',
    };

    static BREAKPOINTS = {
      MOBILE: 768,
    };

    static VIDEO_MARKERS = {
      START: 'variantVideosStart',
      END: 'variantVideosEnd',
    };

    static YOUTUBE = {
      PATTERNS: [
        /https?:\/\/(?:www\.)?youtube\.com\/shorts\/([a-zA-Z0-9_-]+)/g,
        /https?:\/\/(?:www\.)?youtube\.com\/watch\?v=([a-zA-Z0-9_-]+)/g,
        /https?:\/\/youtu\.be\/([a-zA-Z0-9_-]+)/g,
        /https?:\/\/(?:www\.)?youtube\.com\/embed\/([a-zA-Z0-9_-]+)/g,
      ],
      EMBED_BASE: 'https://www.youtube.com/embed/',
      ORIGIN: 'https://www.youtube.com',
    };

    static TIMING = {
      VARIANT_CHANGE_DELAY: 200,
      MUTATION_DELAY: 100,
      RESIZE_DEBOUNCE: 200,
      SCROLL_DEBOUNCE: 100,
      INIT_CHECK_DELAY: 1000,
      VIDEO_LOAD_TIMEOUT: 3000,
      PLAY_DELAY: 300,
      UNMUTE_DELAY: 200,
      SCROLL_PLAY_DELAY: 500,
    };

    static LAYOUT = {
      PC_COLUMNS: 4,
      PC_GAP: 20,
      MOBILE_GAP: 15,
      SCROLL_TOLERANCE: 5,
    };

    static isMobile() {
      return window.innerWidth <= this.BREAKPOINTS.MOBILE;
    }
  }

  /**
   * 视频提取器 - 从描述内容中提取 YouTube 视频链接
   */
  class VideoExtractor {
    /**
     * 从描述容器中提取 YouTube 视频 URLs 和标题
     */
    static extractFromContainer(container) {
      const html = container.innerHTML;
      
      console.log('检查视频标记...');
      
      if (!this.hasVideoMarkers(html)) {
        console.log('未找到视频标记');
        return { urls: [], title: null };
      }

      const videoSection = this.extractVideoSection(html);
      if (!videoSection) {
        console.log('无法提取视频区域');
        return { urls: [], title: null };
      }

      console.log('提取的视频区域内容:', videoSection.substring(0, 300));
      
      const urls = this.extractYouTubeUrls(videoSection);
      const title = this.extractTitle(videoSection);
      
      console.log(`总共找到 ${urls.length} 个视频链接`);
      if (title) {
        console.log('找到标题:', title);
      }
      
      return { urls, title };
    }
    
    /**
     * 从视频区域中提取标题
     * 支持格式：
     * <!-- 标题文本 -->
     * <!-- <h2>标题文本</h2> -->
     */
    static extractTitle(videoSection) {
      // 匹配注释中的标题
      // 格式1: <!-- 标题文本 --> (在视频链接之前)
      // 格式2: <!-- <h2>标题文本</h2> -->
      const titlePatterns = [
        /<!--\s*<h2[^>]*>(.*?)<\/h2>\s*-->/i,  // <!-- <h2>标题</h2> -->
        /<!--\s*([^<>\n]+?)\s*-->/  // <!-- 标题文本 -->
      ];
      
      // 先尝试匹配 h2 标签格式
      for (const pattern of titlePatterns) {
        pattern.lastIndex = 0;
        const match = pattern.exec(videoSection);
        if (match) {
          const titleText = match[1].trim();
          // 检查是否在视频链接之前（避免匹配到其他注释）
          const titleIndex = match.index;
          const firstVideoIndex = videoSection.search(/https?:\/\/.*youtube/i);
          
          if (firstVideoIndex === -1 || titleIndex < firstVideoIndex) {
            // 确保不是空的或只是空白
            if (titleText && titleText.length > 0) {
              return titleText;
            }
          }
        }
      }
      
      return null;
    }

    /**
     * 检查是否包含视频标记
     */
    static hasVideoMarkers(html) {
      return html.includes(ConfigManager.VIDEO_MARKERS.START) && 
             html.includes(ConfigManager.VIDEO_MARKERS.END);
    }

    /**
     * 提取视频标记之间的内容
     */
    static extractVideoSection(html) {
      const startIndex = html.indexOf(ConfigManager.VIDEO_MARKERS.START);
      const endIndex = html.indexOf(ConfigManager.VIDEO_MARKERS.END);
      
      if (startIndex === -1 || endIndex === -1 || endIndex <= startIndex) {
        return null;
      }

      return html.substring(startIndex, endIndex);
    }

    /**
     * 从内容中提取 YouTube URLs
     */
    static extractYouTubeUrls(content) {
      const urls = [];
      
      ConfigManager.YOUTUBE.PATTERNS.forEach(pattern => {
        pattern.lastIndex = 0; // 重置正则
        let match;
        while ((match = pattern.exec(content)) !== null) {
          const url = match[0];
          if (!urls.includes(url)) {
            console.log('找到视频链接:', url);
            urls.push(url);
            }
          }
        });
        
      return urls;
    }

    /**
     * 从 URL 提取视频 ID
     */
    static extractVideoId(url) {
      for (const pattern of ConfigManager.YOUTUBE.PATTERNS) {
        pattern.lastIndex = 0;
        const match = url.match(pattern);
        if (match) {
          // 提取第一个捕获组（视频ID）
          const idMatch = url.match(/([a-zA-Z0-9_-]{11})/);
          if (idMatch) return idMatch[1];
        }
      }
      return null;
    }

    /**
     * 隐藏描述中的视频标记
     */
    static hideVideoMarkers(container) {
      const elements = container.querySelectorAll('*');
      const nodesToHide = [];
      
      elements.forEach(element => {
        const html = element.innerHTML || '';
        const text = element.textContent || '';
        
        if (this.containsVideoMarkers(html) || this.containsVideoMarkers(text)) {
          const startIndex = html.indexOf(ConfigManager.VIDEO_MARKERS.START);
          const endIndex = html.indexOf(ConfigManager.VIDEO_MARKERS.END);
          
          if (startIndex !== -1 && endIndex !== -1 && endIndex > startIndex) {
            if (!this.isChildOfHiddenNode(element, nodesToHide)) {
              nodesToHide.push(element);
            }
          }
        }
      });

      // 按深度排序，深的先隐藏
      nodesToHide.sort((a, b) => this.getElementDepth(b) - this.getElementDepth(a));
      nodesToHide.forEach(node => node.style.display = 'none');
    }

    static containsVideoMarkers(text) {
      return text.includes(ConfigManager.VIDEO_MARKERS.START) && 
             text.includes(ConfigManager.VIDEO_MARKERS.END);
    }

    static isChildOfHiddenNode(element, hiddenNodes) {
      return hiddenNodes.some(node => node.contains(element));
    }

    static getElementDepth(element) {
      let depth = 0;
      let current = element;
      while (current.parentElement) {
        depth++;
        current = current.parentElement;
      }
      return depth;
    }
  }

  // ==================== DOMBuilder ====================
  /**
   * DOM构建器 - 负责创建所有DOM元素
   */
  class DOMBuilder {
    /**
     * 创建视频展示区域
     */
    static createVideoSection(videoUrls, title = null) {
      const section = document.createElement('div');
      section.className = 'variant-videos-section';
      section.setAttribute('data-variant-videos', '');
      
      // 创建标题（如果有）
      if (title) {
        const titleElement = this.createTitle(title);
        section.appendChild(titleElement);
      }
      
      const wrapper = document.createElement('div');
      wrapper.className = 'variant-videos-wrapper';
      
      const grid = this.createVideoGrid(videoUrls);
      const indicator = this.createScrollIndicator();
      
      wrapper.appendChild(grid);
      
      // 创建导航按钮（放在 wrapper 中，左右两边）
      const needButtons = ConfigManager.isMobile() || videoUrls.length > ConfigManager.LAYOUT.PC_COLUMNS;
      if (needButtons) {
        // 创建遮罩层阻止穿透
        wrapper.appendChild(this.createNavMask('prev'));
        wrapper.appendChild(this.createNavMask('next'));
        // 创建按钮
        wrapper.appendChild(this.createNavButton('prev'));
        wrapper.appendChild(this.createNavButton('next'));
      }
      
      wrapper.appendChild(indicator);
      section.appendChild(wrapper);

      return section;
    }
    
    /**
     * 创建标题元素
     */
    static createTitle(titleText) {
      const title = document.createElement('h2');
      title.className = 'variant-videos-title';
      title.setAttribute('data-no-after', 'true');  // 标记：去掉 after 伪元素
      title.textContent = titleText;
      return title;
    }

    /**
     * 创建视频网格
     */
    static createVideoGrid(videoUrls) {
      const grid = document.createElement('div');
      grid.className = 'variant-videos-grid';
      
      const isMobile = ConfigManager.isMobile();
      if (!isMobile && videoUrls.length > ConfigManager.LAYOUT.PC_COLUMNS) {
        grid.setAttribute('data-scrollable', 'true');
      }
      
      videoUrls.forEach((url, index) => {
        const videoId = VideoExtractor.extractVideoId(url);
        if (videoId) {
          grid.appendChild(this.createVideoItem(videoId, index, index === 0));
        }
      });
      
      return grid;
    }

    /**
     * 创建单个视频项
     */
    static createVideoItem(videoId, index, isFirst) {
        const item = document.createElement('div');
        item.className = 'variant-video-item';

      const iframe = this.createIframe(videoId, index, isFirst);
      const preview = this.createVideoPreview(videoId);
      
      // 监听加载完成
      iframe.addEventListener('load', () => item.classList.add('loaded'));
      
      // 超时保护
      setTimeout(() => item.classList.add('loaded'), ConfigManager.TIMING.VIDEO_LOAD_TIMEOUT);

      item.appendChild(iframe);
      item.appendChild(preview);
      return item;
    }

    /**
     * 创建 iframe
     */
    static createIframe(videoId, index, isFirst) {
        const iframe = document.createElement('iframe');
      
      // 移动端第一个视频自动播放（静音）
      const autoplayParam = (isFirst && ConfigManager.isMobile()) ? '&autoplay=1&mute=1' : '&mute=1';
      iframe.src = `${ConfigManager.YOUTUBE.EMBED_BASE}${videoId}?enablejsapi=1&rel=0&playsinline=1&modestbranding=1${autoplayParam}`;
        iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
        iframe.allowFullscreen = true;
      iframe.loading = isFirst ? 'eager' : 'lazy';
        iframe.frameBorder = '0';
        iframe.setAttribute('data-video-id', videoId);
      iframe.setAttribute('data-video-index', index);

      return iframe;
    }

    static createVideoPreview(videoId) {
      const preview = document.createElement('button');
      preview.className = 'variant-video-preview';
      preview.setAttribute('type', 'button');
      preview.setAttribute('aria-label', 'Play video');

      const image = document.createElement('img');
      image.alt = '';
      image.loading = 'lazy';
      image.decoding = 'async';

      const sources = this.getThumbnailSources(videoId);
      let currentIndex = 0;

      const applySource = () => {
        image.src = sources[currentIndex];
      };

      image.addEventListener('error', () => {
        currentIndex += 1;

        if (currentIndex < sources.length) {
          applySource();
          return;
        }

        preview.classList.add('thumbnail-error');
        image.remove();
      });

      applySource();
      preview.appendChild(image);

      return preview;
    }

    static getThumbnailSources(videoId) {
      return [
        `https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`,
        `https://i.ytimg.com/vi/${videoId}/sddefault.jpg`,
        `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`,
        `https://i.ytimg.com/vi/${videoId}/mqdefault.jpg`,
      ];
    }

    /**
     * 创建滚动指示器
     */
    static createScrollIndicator() {
      const indicator = document.createElement('div');
      indicator.className = 'variant-videos-scroll-indicator';
      return indicator;
    }

    /**
     * 创建导航按钮遮罩层（防止点击穿透）
     */
    static createNavMask(direction) {
      const mask = document.createElement('div');
      mask.className = `variant-video-nav-mask ${direction}`;
      mask.setAttribute('data-nav-mask', direction);
      return mask;
    }

    /**
     * 创建导航按钮
     */
    static createNavButton(direction) {
      const button = document.createElement('button');
      button.className = `variant-video-nav ${direction}`;
      button.setAttribute('data-video-nav', direction);
      button.setAttribute('type', 'button');
      button.innerHTML = direction === 'prev' 
        ? '<svg viewBox="0 0 24 24"><path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/></svg>'
        : '<svg viewBox="0 0 24 24"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/></svg>';
      
      return button;
    }

    /**
     * 更新滚动指示器（移动端）
     */
    static updateScrollIndicator(indicator, totalCount, activeIndex) {
      if (!ConfigManager.isMobile() || !indicator) return;

      if (indicator.children.length === 0) {
        // 创建指示点
        for (let i = 0; i < totalCount; i++) {
          const dot = document.createElement('div');
          dot.className = 'scroll-dot';
          if (i === 0) dot.classList.add('active');
          indicator.appendChild(dot);
        }
      } else {
        // 更新激活状态
        const dots = indicator.querySelectorAll('.scroll-dot');
        dots.forEach((dot, index) => {
          dot.classList.toggle('active', index === activeIndex);
        });
      }
    }
  }

  // ==================== PlayerController ====================
  /**
   * 播放控制器 - 管理视频播放/暂停
   */
  class PlayerController {
    constructor() {
      this.messageHandlers = [];
      this.currentlyPlaying = null;
    }

    getVideoItem(iframe) {
      return iframe ? iframe.closest(ConfigManager.SELECTORS.VIDEO_ITEM) : null;
    }

    hidePreviewForIframe(iframe) {
      const item = this.getVideoItem(iframe);
      if (item) {
        item.classList.add('is-playing');
      }
    }

    showPreviewForIframe(iframe) {
      const item = this.getVideoItem(iframe);
      if (item) {
        item.classList.remove('is-playing');
      }
    }

    findIframeBySource(sourceWindow) {
      const sections = document.querySelectorAll(ConfigManager.SELECTORS.VIDEO_SECTION);

      for (const section of sections) {
        const iframes = section.querySelectorAll('iframe');
        for (const iframe of iframes) {
          if (iframe.contentWindow === sourceWindow) {
            return iframe;
          }
        }
      }

      return null;
    }

    /**
     * 播放视频
     */
    playVideo(iframe) {
      if (!iframe || !iframe.contentWindow) {
        console.log('iframe 无效');
        return;
      }

      console.log('开始播放视频，video-id:', iframe.getAttribute('data-video-id'));
      
      // PC端：播放前先暂停其他所有视频（多次确保）
      this.hidePreviewForIframe(iframe);
      if (!ConfigManager.isMobile()) {
        this.pauseAllExceptCurrent(iframe);
        // 延迟再次暂停（确保所有视频都收到命令）
        setTimeout(() => {
          this.pauseAllExceptCurrent(iframe);
        }, 50);
      }
      
      this.postMessage(iframe, 'playVideo');
      
      // 延迟取消静音
      setTimeout(() => {
        this.postMessage(iframe, 'unMute');
      }, ConfigManager.TIMING.UNMUTE_DELAY);

      this.currentlyPlaying = iframe;
      
      // PC端：播放后再次确保其他视频暂停
      if (!ConfigManager.isMobile()) {
        setTimeout(() => {
          this.pauseAllExceptCurrent(iframe);
        }, 200);
      }
    }

    /**
     * 暂停视频
     */
    pauseVideo(iframe) {
      if (!iframe || !iframe.contentWindow) return;
      
      this.postMessage(iframe, 'pauseVideo');
      this.showPreviewForIframe(iframe);
    }

    /**
     * 暂停所有视频
     */
    pauseAllVideos(grid) {
      const iframes = grid.querySelectorAll('iframe');
      iframes.forEach(iframe => this.pauseVideo(iframe));
      this.currentlyPlaying = null;
    }

    /**
     * 暂停除指定索引外的所有视频
     */
    pauseAllExcept(grid, activeIndex) {
      const items = grid.querySelectorAll(ConfigManager.SELECTORS.VIDEO_ITEM);
      items.forEach((item, index) => {
        if (index !== activeIndex) {
          const iframe = item.querySelector('iframe');
          if (iframe) this.pauseVideo(iframe);
        }
      });
    }

    /**
     * 播放指定索引的视频
     */
    playVideoAtIndex(grid, index) {
      const items = grid.querySelectorAll(ConfigManager.SELECTORS.VIDEO_ITEM);
      if (items[index]) {
        const iframe = items[index].querySelector('iframe');
        if (iframe) this.playVideo(iframe);
      }
    }

    /**
     * 向 iframe 发送消息
     */
    postMessage(iframe, func, args = []) {
          try {
            iframe.contentWindow.postMessage(
              JSON.stringify({
                event: 'command',
            func: func,
            args: args
              }),
              '*'
            );
          } catch (e) {
        console.log(`无法执行 ${func}:`, e);
      }
    }

    /**
     * 暂停除指定 iframe 外的所有视频（PC端）
     */
    pauseAllExceptCurrent(currentIframe) {
      if (ConfigManager.isMobile()) return;
      
      console.log('PC端：暂停除当前视频外的所有视频');
      
      const allSections = document.querySelectorAll(ConfigManager.SELECTORS.VIDEO_SECTION);
      allSections.forEach(section => {
        const iframes = section.querySelectorAll('iframe');
        iframes.forEach(iframe => {
          if (iframe !== currentIframe && iframe.contentWindow) {
            this.pauseVideo(iframe);
          }
        });
      });
    }

    /**
     * 设置播放控制（PC端：确保同时只播放一个）
     */
    setupPlaybackControl(grid) {
      if (ConfigManager.isMobile()) return;  // 移动端不需要这个控制
      
      const messageHandler = (event) => {
        if (event.origin !== ConfigManager.YOUTUBE.ORIGIN) return;
        
        try {
          const data = JSON.parse(event.data);
          
          // 当视频开始播放时（状态码 1）
          if (data.event === 'onStateChange' && data.info === 1) {
            console.log('PC端：检测到视频播放事件，暂停其他视频');
            
            // 找到触发事件的 iframe
            const allSections = document.querySelectorAll(ConfigManager.SELECTORS.VIDEO_SECTION);
            let currentIframe = null;
            
            allSections.forEach(section => {
              const iframes = section.querySelectorAll('iframe');
              iframes.forEach(iframe => {
                if (iframe.contentWindow === event.source) {
                  currentIframe = iframe;
                }
              });
            });
            
            if (currentIframe) {
              // 立即暂停其他视频
              this.pauseAllExceptCurrent(currentIframe);
              
              // 延迟再次检查（确保所有视频都收到暂停命令）
              setTimeout(() => {
                this.pauseAllExceptCurrent(currentIframe);
              }, 100);
            }
          }
        } catch (e) {
          // 忽略非 JSON 消息
        }
      };
      
      window.addEventListener('message', messageHandler);
      this.messageHandlers.push(messageHandler);
      
      // 为每个视频项添加点击事件监听（覆盖整个区域）
      const items = grid.querySelectorAll(ConfigManager.SELECTORS.VIDEO_ITEM);
      items.forEach((item, index) => {
        const iframe = item.querySelector('iframe');
        if (!iframe) return;
        
        const clickHandler = (e) => {
          // 立即暂停其他视频
          console.log(`PC端：检测到视频 ${index} 被点击，暂停其他视频`);
          this.pauseAllExceptCurrent(iframe);
          
          // 延迟再次检查（确保 YouTube 播放事件触发后）
          setTimeout(() => {
            this.pauseAllExceptCurrent(iframe);
          }, 300);
          
          // 再次延迟检查（确保所有异步操作完成）
          setTimeout(() => {
            this.pauseAllExceptCurrent(iframe);
          }, 600);
        };
        
        item.addEventListener('click', clickHandler, true);  // 使用捕获阶段
        // 保存处理器以便清理
        if (!item.__clickHandler) {
          item.__clickHandler = clickHandler;
        }
      });
      
      // 通知所有 iframe 接收状态更新（多次尝试，确保成功）
      const setupListening = () => {
        const iframes = grid.querySelectorAll('iframe');
        console.log(`PC端：为 ${iframes.length} 个视频设置播放监听`);
        
        iframes.forEach((iframe, index) => {
          try {
            if (iframe.contentWindow) {
              iframe.contentWindow.postMessage(
                JSON.stringify({
                  event: 'listening',
                  id: iframe.getAttribute('data-video-id'),
                  channel: 'widget'
                }),
                '*'
              );
              console.log(`PC端：已为视频 ${index} 设置监听`);
            }
          } catch (e) {
            console.log(`PC端：无法设置视频 ${index} 监听:`, e);
          }
        });
      };
      
      // 立即设置
      setTimeout(setupListening, ConfigManager.TIMING.INIT_CHECK_DELAY);
      
      // 延迟再次设置（确保 iframe 完全加载）
      setTimeout(setupListening, ConfigManager.TIMING.INIT_CHECK_DELAY + 1000);
    }

    /**
     * 清理消息监听器
     */
    bindPreviewInteractions(grid) {
      const items = grid.querySelectorAll(ConfigManager.SELECTORS.VIDEO_ITEM);

      items.forEach(item => {
        const iframe = item.querySelector('iframe');
        const preview = item.querySelector(ConfigManager.SELECTORS.VIDEO_PREVIEW);

        if (!iframe || !preview || preview.__boundPreviewClick) return;

        const previewHandler = (e) => {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();

          this.pauseAllVideos(grid);
          this.playVideo(iframe);
        };

        preview.addEventListener('click', previewHandler);
        preview.__boundPreviewClick = true;
      });
    }

    cleanup() {
      this.messageHandlers.forEach(handler => {
        window.removeEventListener('message', handler);
      });
      this.messageHandlers = [];
      this.currentlyPlaying = null;
    }
  }

  // ==================== NavigationController ====================
  /**
   * 导航控制器 - 处理按钮和滚动
   */
  class NavigationController {
    constructor(playerController, viewportObserver) {
      this.playerController = playerController;
      this.viewportObserver = viewportObserver;
      this.scrollHandlers = new Map();
      this.scrollEndTimers = new Map();  // 存储滚动结束定时器
      this.lastPlayedIndex = -1;  // 上次播放的视频索引
    }

    /**
     * 初始化导航按钮
     */
    initNavigation(section) {
      const grid = section.querySelector(ConfigManager.SELECTORS.VIDEO_GRID);
      const wrapper = section.querySelector('.variant-videos-wrapper');
      const prevBtn = section.querySelector(ConfigManager.SELECTORS.PREV_BUTTON);
      const nextBtn = section.querySelector(ConfigManager.SELECTORS.NEXT_BUTTON);
      
      if (!grid) return;

      // 绑定按钮事件
      if (prevBtn) this.bindButtonEvents(prevBtn, grid, 'prev');
      if (nextBtn) this.bindButtonEvents(nextBtn, grid, 'next');

      // 为遮罩层添加事件拦截
      if (wrapper) {
        const masks = wrapper.querySelectorAll('[data-nav-mask]');
        masks.forEach(mask => {
          this.bindMaskEvents(mask);
        });
      }

      // 初始化滚动监听
      this.initScrollListener(section);

      // 更新按钮状态
      this.updateButtonStates(section);
    }

    /**
     * 绑定遮罩层事件（阻止所有事件穿透）
     */
    bindMaskEvents(mask) {
      const blockEvent = (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
      };

      // 阻止所有可能的触摸和点击事件
      ['touchstart', 'touchmove', 'touchend', 'mousedown', 'click'].forEach(eventType => {
        mask.addEventListener(eventType, blockEvent, { passive: false, capture: true });
      });
    }

    /**
     * 绑定按钮事件
     */
    bindButtonEvents(button, grid, direction) {
      const handleClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        console.log(`${direction} 按钮被点击`);
        
        const items = grid.querySelectorAll(ConfigManager.SELECTORS.VIDEO_ITEM);
        if (items.length === 0) return;
        
        // 暂停所有视频
        this.playerController.pauseAllVideos(grid);
        
        // 计算滚动距离
        const itemWidth = items[0].offsetWidth;
        const gap = ConfigManager.isMobile() ? ConfigManager.LAYOUT.MOBILE_GAP : ConfigManager.LAYOUT.PC_GAP;
        const scrollAmount = direction === 'prev' ? -(itemWidth + gap) : (itemWidth + gap);
        
        // 计算目标索引
        const currentIndex = this.getCurrentVideoIndex(grid);
        const targetIndex = direction === 'prev' ? Math.max(0, currentIndex - 1) : Math.min(items.length - 1, currentIndex + 1);
        
        grid.scrollBy({
          left: scrollAmount,
          behavior: 'smooth'
        });
        
        // 移动端：滚动完成后播放目标视频
        if (ConfigManager.isMobile()) {
      setTimeout(() => {
            const finalIndex = this.getCurrentVideoIndex(grid);
            if (this.viewportObserver.isSectionInViewport()) {
              console.log(`移动端：按钮切换后播放视频 ${finalIndex}`);
              this.playerController.pauseAllExcept(grid, finalIndex);
              setTimeout(() => {
                this.playerController.playVideoAtIndex(grid, finalIndex);
                this.lastPlayedIndex = finalIndex;
              }, 300);
            }
          }, 600);  // 等待滚动动画完成
        }
      };

      // 使用捕获阶段拦截所有触摸事件
      button.addEventListener('touchstart', (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
      }, { passive: false, capture: true });

      button.addEventListener('touchend', (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        handleClick(e);
      }, { passive: false, capture: true });

      // PC端点击
      button.addEventListener('click', handleClick);
    }

    /**
     * 初始化滚动监听
     */
    initScrollListener(section) {
      const grid = section.querySelector(ConfigManager.SELECTORS.VIDEO_GRID);
      const indicator = section.querySelector(ConfigManager.SELECTORS.SCROLL_INDICATOR);
      
      if (!grid) return;

      // 移除旧的监听器
      this.removeScrollHandler(grid);

      const items = grid.querySelectorAll(ConfigManager.SELECTORS.VIDEO_ITEM);
      if (items.length === 0) return;

      // 初始化指示器
      if (ConfigManager.isMobile() && indicator) {
        DOMBuilder.updateScrollIndicator(indicator, items.length, 0);
      }

      // 移动端：初始化时如果区域在视口内，播放第一个视频
      if (ConfigManager.isMobile() && this.viewportObserver.isSectionInViewport()) {
        setTimeout(() => {
          console.log('移动端：初始化时播放第一个视频');
          this.playerController.playVideoAtIndex(grid, 0);
          this.lastPlayedIndex = 0;
        }, ConfigManager.TIMING.INIT_CHECK_DELAY);
      }

      let scrollTimeout;
      const scrollHandler = () => {
        clearTimeout(scrollTimeout);
        
        // 清除之前的滚动结束定时器
        const endTimer = this.scrollEndTimers.get(grid);
        if (endTimer) clearTimeout(endTimer);
        
        scrollTimeout = setTimeout(() => {
          const currentIndex = this.getCurrentVideoIndex(grid);
          
          // 更新指示器
          if (ConfigManager.isMobile() && indicator) {
            DOMBuilder.updateScrollIndicator(indicator, items.length, currentIndex);
          }
          
          // 暂停其他视频
          this.playerController.pauseAllExcept(grid, currentIndex);
          
          // 移动端：滚动停止后播放当前视频（如果区域在视口内且索引改变）
          if (ConfigManager.isMobile() && this.viewportObserver.isSectionInViewport()) {
            if (currentIndex !== this.lastPlayedIndex) {
              const playTimer = setTimeout(() => {
                console.log(`移动端：滚动停止，播放视频 ${currentIndex}`);
                this.playerController.playVideoAtIndex(grid, currentIndex);
                this.lastPlayedIndex = currentIndex;
              }, 200);  // 滚动停止200ms后播放
              this.scrollEndTimers.set(grid, playTimer);
            }
          }
          
          // 更新按钮状态
          this.updateButtonStates(section);
        }, ConfigManager.TIMING.SCROLL_DEBOUNCE);
      };

      grid.addEventListener('scroll', scrollHandler);
      this.scrollHandlers.set(grid, { element: grid, handler: scrollHandler });
    }

    /**
     * 获取当前视频索引
     */
    getCurrentVideoIndex(grid) {
      const scrollLeft = grid.scrollLeft;
      const itemWidth = grid.querySelector(ConfigManager.SELECTORS.VIDEO_ITEM)?.offsetWidth || 0;
      const gap = ConfigManager.isMobile() ? ConfigManager.LAYOUT.MOBILE_GAP : ConfigManager.LAYOUT.PC_GAP;
      return Math.round(scrollLeft / (itemWidth + gap));
    }

    /**
     * 更新按钮状态
     */
    updateButtonStates(section) {
      const grid = section.querySelector(ConfigManager.SELECTORS.VIDEO_GRID);
      const prevBtn = section.querySelector(ConfigManager.SELECTORS.PREV_BUTTON);
      const nextBtn = section.querySelector(ConfigManager.SELECTORS.NEXT_BUTTON);
      
      if (!grid) return;

      const scrollLeft = grid.scrollLeft;
      const maxScroll = grid.scrollWidth - grid.clientWidth;
      
      if (prevBtn) prevBtn.disabled = scrollLeft <= 0;
      if (nextBtn) nextBtn.disabled = scrollLeft >= maxScroll - ConfigManager.LAYOUT.SCROLL_TOLERANCE;
    }

    /**
     * 显示导航按钮
     */
    showNavigation(section) {
        section.classList.add('show-nav');
      console.log('导航按钮已显示');
    }

    /**
     * 移除滚动处理器
     */
    removeScrollHandler(grid) {
      const handler = this.scrollHandlers.get(grid);
      if (handler && handler.element) {
        handler.element.removeEventListener('scroll', handler.handler);
        this.scrollHandlers.delete(grid);
      }
    }

    /**
     * 清理所有滚动处理器
     */
    cleanup() {
      this.scrollHandlers.forEach(({ element, handler }) => {
        if (element && handler) {
          element.removeEventListener('scroll', handler);
        }
      });
      this.scrollHandlers.clear();
      
      // 清理滚动结束定时器
      this.scrollEndTimers.forEach(timer => clearTimeout(timer));
      this.scrollEndTimers.clear();
    }
  }

  // ==================== ViewportObserver ====================
  /**
   * 视口观察器 - 移动端自动播放控制
   */
  class ViewportObserver {
    constructor(playerController) {
      this.playerController = playerController;
      this.observers = [];
      this.sectionInViewport = false;  // 区域是否在视口内
    }

    /**
     * 设置视口观察（移动端）- 只用于检测区域是否在视口内
     */
    setupObserver(section, grid) {
      if (!ConfigManager.isMobile()) return;

      const observerOptions = {
        root: null,
        rootMargin: '0px',
        threshold: 0.1  // 10%可见即认为在视口内
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          this.sectionInViewport = entry.isIntersecting;
          
          if (!entry.isIntersecting) {
            // 区域离开视口，暂停所有视频
            console.log('移动端：视频区域离开视口，暂停所有视频');
            this.playerController.pauseAllVideos(grid);
          }
          // 进入视口时不处理，由滚动事件和按钮点击触发播放
        });
      }, observerOptions);

      observer.observe(section);
      this.observers.push(observer);

      // 初始化检查
      setTimeout(() => {
        const rect = section.getBoundingClientRect();
        this.sectionInViewport = rect.top < window.innerHeight && rect.bottom > 0;
        
        if (this.sectionInViewport) {
          console.log('移动端：初始化时视频区域在视口内');
          // 初始化播放由滚动监听器处理
        }
      }, ConfigManager.TIMING.INIT_CHECK_DELAY);
    }
    
    /**
     * 检查区域是否在视口内
     */
    isSectionInViewport() {
      return this.sectionInViewport;
    }

    /**
     * 获取当前视频索引
     */
    getCurrentIndex(grid) {
      const scrollLeft = grid.scrollLeft;
      const itemWidth = grid.querySelector(ConfigManager.SELECTORS.VIDEO_ITEM)?.offsetWidth || 0;
      const gap = ConfigManager.LAYOUT.MOBILE_GAP;
      return Math.round(scrollLeft / (itemWidth + gap));
    }

    /**
     * 清理观察器
     */
    cleanup() {
      this.observers.forEach(observer => observer.disconnect());
      this.observers = [];
      this.sectionInViewport = false;
    }
  }

  // ==================== ResourceManager ====================
  /**
   * 资源管理器 - 统一管理资源清理
   */
  class ResourceManager {
    constructor() {
      this.timers = [];
      this.eventHandlers = new Map();
      this.mutationObserver = null;
    }

    /**
     * 添加定时器
     */
    addTimer(timer) {
      this.timers.push(timer);
      return timer;
    }

    /**
     * 添加事件处理器
     */
    addEventHandler(event, handler, target = document) {
      if (!this.eventHandlers.has(event)) {
        this.eventHandlers.set(event, []);
      }
      this.eventHandlers.get(event).push({ target, handler });
      target.addEventListener(event, handler);
    }

    /**
     * 设置 MutationObserver
     */
    setupMutationObserver(callback, target, options) {
      if (this.mutationObserver) {
        this.mutationObserver.disconnect();
      }
      
      this.mutationObserver = new MutationObserver(callback);
      this.mutationObserver.observe(target, options);
    }

    /**
     * 清理所有资源
     */
    cleanup() {
      // 清理定时器
      this.timers.forEach(timer => clearTimeout(timer));
      this.timers = [];

      // 清理事件监听器
      this.eventHandlers.forEach((handlers, event) => {
        handlers.forEach(({ target, handler }) => {
          target.removeEventListener(event, handler);
        });
      });
      this.eventHandlers.clear();

      // 断开 MutationObserver
      if (this.mutationObserver) {
        this.mutationObserver.disconnect();
        this.mutationObserver = null;
      }
    }
  }

  // ==================== VideoManager ====================
  /**
   * 视频管理器 - 主控制器，协调所有模块
   */
  class VideoManager {
    constructor() {
      this.processed = false;
      this.isProcessing = false;  // 添加处理标志，防止循环
      this.resourceManager = new ResourceManager();
      this.playerController = new PlayerController();
      this.viewportObserver = new ViewportObserver(this.playerController);
      this.navigationController = new NavigationController(this.playerController, this.viewportObserver);
      
      this.init();
    }

    /**
     * 初始化
     */
    init() {
      if (document.readyState === 'loading') {
        const handler = () => {
          this.processDescription();
          this.setupListeners();
        };
        this.resourceManager.addEventHandler('DOMContentLoaded', handler);
      } else {
        this.processDescription();
        this.setupListeners();
      }
    }

    /**
     * 设置事件监听
     */
    setupListeners() {
      // 监听变体切换（备用机制，主要由 auxito-variant-gallery.js 触发）
      const variantHandler = () => {
        console.log('检测到变体切换事件（variant:changed）');
        this.processed = false;
        
        // 延迟处理，让 auxito-variant-gallery.js 先更新 innerHTML
        const timer = setTimeout(() => {
          this.processDescription();
        }, ConfigManager.TIMING.VARIANT_CHANGE_DELAY);
        this.resourceManager.addTimer(timer);
      };
      this.resourceManager.addEventHandler('variant:changed', variantHandler);

      // 设置 MutationObserver（监控描述容器变化）
      this.setupMutationObserver();

      // 响应式处理
      let resizeTimer;
      const resizeHandler = () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          this.reinitAllSections();
        }, ConfigManager.TIMING.RESIZE_DEBOUNCE);
        this.resourceManager.addTimer(resizeTimer);
      };
      this.resourceManager.addEventHandler('resize', resizeHandler, window);
    }

    /**
     * 设置 MutationObserver
     */
    setupMutationObserver() {
      const descContainer = document.querySelector(ConfigManager.SELECTORS.DESCRIPTION_CONTAINER);
      if (!descContainer) return;

      let mutationTimer = null;

      const callback = (mutations) => {
        // 如果正在处理中，忽略变化
        if (this.isProcessing) {
          console.log('正在处理中，忽略 Mutation');
          return;
        }

        let hasSignificantChange = false;
        let isOwnChange = false;

        mutations.forEach(mutation => {
          if (mutation.type === 'childList') {
            // 检查是否是自己创建的视频区域
            Array.from(mutation.addedNodes).forEach(node => {
              if (node.nodeType === 1 && node.hasAttribute && node.hasAttribute('data-variant-videos')) {
                isOwnChange = true;
              }
            });

            // 检测是否有新的内容添加（但排除自己创建的）
            if (!isOwnChange && mutation.addedNodes.length > 0) {
              const hasVideoMarkers = Array.from(mutation.addedNodes).some(node => {
                if (node.nodeType === 1 && !node.hasAttribute('data-variant-videos')) {
                  const html = node.innerHTML || '';
                  return html.includes('variantVideosStart') || html.includes('variantVideosEnd');
                }
                return false;
              });
              
              if (mutation.addedNodes.length > 3 || hasVideoMarkers ||
                  Array.from(mutation.addedNodes).some(node => 
                    node.nodeType === 1 && 
                    !node.hasAttribute('data-variant-videos') && 
                    node.children.length > 0
                  )) {
                hasSignificantChange = true;
              }
            }
          }
        });
        
        if (hasSignificantChange && !isOwnChange) {
          console.log('检测到外部描述内容变化');
          // 清除之前的定时器
          if (mutationTimer) {
            clearTimeout(mutationTimer);
          }
          // 重置处理标志
          this.processed = false;
          mutationTimer = setTimeout(() => {
            this.processDescription();
            this.processed = true;
          }, ConfigManager.TIMING.MUTATION_DELAY);
          this.resourceManager.addTimer(mutationTimer);
        }
      };

      this.resourceManager.setupMutationObserver(callback, descContainer, {
        childList: true,
        subtree: true
      });
    }

    /**
     * 处理描述内容（公共方法，可被外部调用）
     */
    processDescription() {
      // 防止重复处理
      if (this.isProcessing) {
        console.log('已在处理中，跳过');
        return;
      }

      this.isProcessing = true;
      console.log('开始处理描述内容...');
      
      const descContainer = document.querySelector(ConfigManager.SELECTORS.DESCRIPTION_CONTAINER);
      if (!descContainer) {
        console.log('未找到描述容器');
        this.isProcessing = false;
        return;
      }

      // 提取视频链接和标题
      const { urls: videoUrls, title } = VideoExtractor.extractFromContainer(descContainer);
      
      // 移除已存在的视频区域
      const existingSection = descContainer.querySelector(ConfigManager.SELECTORS.VIDEO_SECTION);
      if (existingSection) {
        console.log('移除已存在的视频区域');
        const grid = existingSection.querySelector(ConfigManager.SELECTORS.VIDEO_GRID);
        if (grid) {
          this.navigationController.removeScrollHandler(grid);
        }
        // 清理视口观察器（播放控制器在 setupPlaybackControl 中清理）
        this.viewportObserver.cleanup();
        existingSection.remove();
      }

      if (videoUrls.length > 0) {
        console.log(`找到 ${videoUrls.length} 个视频:`, videoUrls);
        
        // 创建并插入视频区域（包含标题）
        const videoSection = DOMBuilder.createVideoSection(videoUrls, title);
        descContainer.insertBefore(videoSection, descContainer.firstChild);
        
        console.log('视频区域已插入到描述容器');
        
        // 隐藏描述中的视频标记
        VideoExtractor.hideVideoMarkers(descContainer);
        
        // 初始化功能
        const timer = setTimeout(() => {
          this.initVideoSection(videoSection);
          this.isProcessing = false;  // 完成后重置标志
        }, ConfigManager.TIMING.MUTATION_DELAY);
        this.resourceManager.addTimer(timer);
      } else {
        console.log('未找到视频链接，检查描述内容');
        this.isProcessing = false;  // 完成后重置标志
      }
    }

    /**
     * 初始化视频区域
     */
    initVideoSection(section) {
      const grid = section.querySelector(ConfigManager.SELECTORS.VIDEO_GRID);
      const wrapper = section.querySelector('.variant-videos-wrapper');
      
      if (!grid || !wrapper) return;

      // 初始化导航
      this.navigationController.initNavigation(section);

      // 设置播放控制（PC端）
      this.playerController.setupPlaybackControl(grid);
      this.playerController.bindPreviewInteractions(grid);

      // 设置视口观察（移动端）
      this.viewportObserver.setupObserver(section, grid);

      // 等待视频加载完成后显示导航
      this.checkVideosLoaded(grid, section);
    }

    /**
     * 检查视频加载完成
     */
    checkVideosLoaded(grid, section) {
      const items = grid.querySelectorAll(ConfigManager.SELECTORS.VIDEO_ITEM);
      let loadedCount = 0;
      const totalCount = items.length;

      const checkComplete = () => {
        if (loadedCount >= totalCount) {
          this.navigationController.showNavigation(section);
          console.log('所有视频加载完成');
        }
      };

      items.forEach((item, index) => {
        const iframe = item.querySelector('iframe');
        if (iframe) {
          iframe.addEventListener('load', () => {
            loadedCount++;
            console.log(`视频 ${index + 1}/${totalCount} 加载完成`);
            checkComplete();
          });
        }
      });

      // 超时保护
      const timer = setTimeout(() => {
        console.log('超时保护：强制显示导航');
        this.navigationController.showNavigation(section);
      }, ConfigManager.TIMING.VIDEO_LOAD_TIMEOUT);
      this.resourceManager.addTimer(timer);
    }

    /**
     * 重新初始化所有视频区域（响应式）
     */
    reinitAllSections() {
      const sections = document.querySelectorAll(ConfigManager.SELECTORS.VIDEO_SECTION);
      sections.forEach(section => {
        this.navigationController.initNavigation(section);
      });
    }

    /**
     * 清理所有资源
     */
    cleanup() {
      this.resourceManager.cleanup();
      this.playerController.cleanup();
      this.navigationController.cleanup();
      this.viewportObserver.cleanup();
    }
  }

  // ==================== 单例初始化 ====================
  if (!window.variantVideosInstance) {
    window.variantVideosInstance = new VideoManager();
    
    // 页面卸载时清理
    window.addEventListener('beforeunload', () => {
      if (window.variantVideosInstance) {
        window.variantVideosInstance.cleanup();
        window.variantVideosInstance = null;
      }
    });
  }
})();
